/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment-timezone";
// components imports
import { Button, Form, Input, Select, message } from "antd";
import { SaveOutlined } from "@ant-design/icons";

import { ErrorsComponent } from "../../UI/ErrorsComponent";

// misc imports
import { useUserInfo, usePatchUser } from "@/api/user";
import { validatePassword } from "@/utils/form/validators";
import { NAV_PROFILE } from "@/constants";
import { ITokens, userInterfaceKeys } from "@/interfaces/User.interface";
import { IRequestError } from "@/interfaces/ServerError.interface";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { tokens as Tokens } from "@/utils/tokens";
import { queryClient } from "@/utils/queryClient";

type FieldType = {
  first_name?: string;
  last_name?: string;
  company?: string;
  email?: string;
  password?: string;
  confirm_password?: string;
  timezone?: string;
};

const { Option } = Select;

/**
 * Edit profile form component
 * @returns {JSX.Element}
 */
export const EditProfileForm = (): JSX.Element => {
  const timezoneOptions = moment.tz.names();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const tokensInstance = Tokens;
  const userAuth:ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken } = userAuth || {};
  
  const [messageApi, contextHolder] = message.useMessage();
  const { data: userData } = useUserInfo(tokensInstance.getTokenSub(accessToken) as string);
  const { mutate: patchUser, isLoading, error } = usePatchUser();

  useEffect(() => {
    form.setFieldsValue(userData);
    if (userData && !userData.timezone) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      form.setFieldValue("timezone", tz);
    }
  }, [form, userData]);

  const validateForm = async (values: Record<string, any>) => {
    // Trim the form fields
    const trimmedValues: Record<string, any> = {};

    for (const key in values) {
      // if (values.hasOwnProperty(key)) {
      const value = values[key];
      if (typeof value === "string") {
        trimmedValues[key] = value.trim();
      } else {
        trimmedValues[key] = value;
      }
      // }
    }

    // Update the form values with the trimmed values
    form.setFieldsValue(trimmedValues);
    await form.validateFields();
  };

  const validateMessages = {
    required: t("validation.required"),
    types: {
      email: t("validation.email"),
    },
    password: {
      range: t("validation.password_length"),
    },
  };

  const onFinish = async (values: Record<string, any>) => {
    try {
      await validateForm(values);
    } catch (e) {
      return; // Return as we have an error at a form field
    }

    const valuesToSubmit: Partial<FieldType> = {};

    // Validate and submit
    Object.entries(values).forEach(([key, value]) => {
      if (userInterfaceKeys.includes(key) && value !== undefined && (!userData || (userData && value !== userData[key]))) {
        valuesToSubmit[key as keyof FieldType] = value;
      }
    });

    // Now you can safely submit the form
    patchUser(
      {
        userId: userData?.uuid as string,
        userData: { ...valuesToSubmit },
      },
      {
        onSuccess: () => {
          messageApi.open({
            content: t("edit_user.success_message"),
            type: "success",
            duration: 8,
          });
          setTimeout(() => {
            navigate(
              replaceUrlParams(NAV_PROFILE, {
                workspaceId: workspaceId as string,
              })
            );
          });
        },
      }
    );
  };

  // Validator for Password field
  const handlePasswordValidator = async (_rule: any, value: string) => {
    const errorKey = validatePassword(value, false);
    const formValues = form.getFieldsValue();

    if (errorKey) {
      return Promise.reject(t(errorKey));
    }

    // Check if the passwords match
    if (
      formValues.password &&
      formValues.password.trim() !== "" &&
      formValues.confirm_password &&
      formValues.confirm_password.trim() !== "" &&
      formValues.password !== formValues.confirm_password
    ) {
      return Promise.reject(t("validation.password_match"));
    }

    return Promise.resolve();
  };

  return (
    <>
      {contextHolder}
      <Form
        name="edit-profile-form"
        labelAlign="left"
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        validateMessages={validateMessages}
        form={form} // Add the form instance
      >
        {/* First Name */}
        <Form.Item<FieldType>
          label={t("common.first_name")}
          name="first_name"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input />
        </Form.Item>
        {/* ./ First Name */}
        {/* Last Name */}
        <Form.Item<FieldType>
          label={t("common.last_name")}
          name="last_name"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input />
        </Form.Item>
        {/* ./ Last Name */}
        {/* Company */}
        <Form.Item<FieldType> label={t("common.company")} name="company">
          <Input />
        </Form.Item>
        {/* ./ Company */}
        {/* Email */}
        <Form.Item<FieldType>
          label={t("common.email")}
          name="email"
          rules={[
            {
              required: true,
              type: "email",
            },
          ]}
          initialValue={userData?.email}
        >
          <Input disabled />
        </Form.Item>
        {/* ./ Email */}
        {/* Password */}
        <Form.Item<FieldType> label={t("common.password")} name="password" rules={[{ validator: handlePasswordValidator }]}>
          <Input.Password autoComplete="new-password" onBlur={() => form.validateFields(["confirm_password"])} />
        </Form.Item>
        {/* ./ Password */}
        {/* Confirm Password */}
        <Form.Item<FieldType> label={t("common.confirm_password")} name="confirm_password" rules={[{ validator: handlePasswordValidator }]}>
          <Input.Password onBlur={() => form.validateFields(["password"])} />
        </Form.Item>
        {/* ./ Confirm Password */}

        {/* Timezone options */}
        <Form.Item<FieldType> label={t("common.timezone")} name="timezone">
          <Select showSearch placeholder={t("common.select_option_placeholder")}>
            {timezoneOptions?.map((timezone) => (
              <Option key={timezone} value={timezone}>
                {timezone}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {/* ./ Timezone options */}

        <Form.Item
          className="mb-0"
        >
          <Button icon={<SaveOutlined />} disabled={isLoading} type="primary" htmlType="submit">
            {t("common.save")}
          </Button>
          <ErrorsComponent error={error as unknown as IRequestError} />
        </Form.Item>
      </Form>
    </>
  );
};
